/**
 * Pluggable `TokenStore` (03-05 §3) — the iOS PWA driver (R-05).
 *
 * Two modes behind one interface:
 *  - `cookie` (default desktop): access token in memory; refresh rides an
 *    HttpOnly cookie the browser sends automatically. Nothing is persisted
 *    client-side, so `loadPersisted()` is always `null`.
 *  - `bearer` (installed iOS PWA): tokens persisted to `localStorage` and sent
 *    in request bodies/headers, because HttpOnly cookies and sessionStorage are
 *    unreliable across relaunches in standalone WebKit. Backend support for
 *    body/header tokens is Q-06 (confirmed 2026-07-07).
 */

/** Token set the backend issues. `expiresAt` is epoch ms when the backend exposes it. */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt?: number;
}

export type TokenStoreMode = 'cookie' | 'bearer';

export interface TokenStore {
  readonly mode: TokenStoreMode;
  /** Current access token, or null when none is held (cookie mode keeps it in memory). */
  getAccessToken(): string | null;
  /** Replace the held tokens; `null` clears them (and any persisted copy). */
  setTokens(tokens: AuthTokens | null): void;
  /** Persisted tokens surviving a relaunch (bearer only); always `null` in cookie mode. */
  loadPersisted(): AuthTokens | null;
}

const STORAGE_KEY = 'smartsash.auth.tokens';

/** A minimal `localStorage`-shaped surface, so tests can inject a fake. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isAuthTokens(value: unknown): value is AuthTokens {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.accessToken === 'string' && typeof candidate.refreshToken === 'string';
}

/** `window.localStorage` when available, else a null store (SSR / privacy-mode safe). */
function defaultStorage(): KeyValueStorage | null {
  try {
    if (typeof window !== 'undefined') return window.localStorage;
  } catch {
    // Accessing localStorage can throw in sandboxed/privacy contexts.
  }
  return null;
}

/**
 * Cookie mode: the browser owns the refresh via an HttpOnly cookie. We keep the
 * access token in memory only (never persisted) and never emit it as a header —
 * the HTTP bridge suppresses `Authorization` in cookie mode.
 */
export function createCookieTokenStore(): TokenStore {
  let accessToken: string | null = null;
  return {
    mode: 'cookie',
    getAccessToken: () => accessToken,
    setTokens: (tokens) => {
      accessToken = tokens?.accessToken ?? null;
    },
    loadPersisted: () => null,
  };
}

/**
 * Bearer mode: tokens persist to `localStorage` so an installed PWA survives a
 * force-quit/relaunch. Memory is hydrated from storage on construction, so
 * `getAccessToken()` works immediately after a relaunch.
 */
export function createBearerTokenStore(
  storage: KeyValueStorage | null = defaultStorage(),
): TokenStore {
  const read = (): AuthTokens | null => {
    if (!storage) return null;
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isAuthTokens(parsed)) return parsed;
    } catch {
      // Corrupt persisted state must not brick sign-in.
    }
    storage.removeItem(STORAGE_KEY);
    return null;
  };

  let current: AuthTokens | null = read();

  return {
    mode: 'bearer',
    getAccessToken: () => current?.accessToken ?? null,
    setTokens: (tokens) => {
      current = tokens;
      if (!storage) return;
      if (tokens) storage.setItem(STORAGE_KEY, JSON.stringify(tokens));
      else storage.removeItem(STORAGE_KEY);
    },
    loadPersisted: read,
  };
}

/** True when running as an installed/standalone PWA (display-mode or iOS `navigator.standalone`). */
export function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // `matchMedia` is typed as always present but can be missing in older/embedded
  // WebViews and jsdom — narrow through a possibly-undefined alias.
  const matchMedia = window.matchMedia as ((query: string) => MediaQueryList) | undefined;
  const displayModeStandalone = matchMedia
    ? matchMedia('(display-mode: standalone)').matches
    : false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeStandalone || iosStandalone;
}

/**
 * Resolve the token store for an app. Pass a `TokenStore` to use it verbatim, a
 * `TokenStoreMode` to force a mode, or nothing to auto-select: standalone PWA →
 * `bearer`, otherwise `cookie`.
 */
export function selectTokenStore(override?: TokenStoreMode | TokenStore): TokenStore {
  if (override && typeof override === 'object') return override;
  const mode: TokenStoreMode = override ?? (detectStandalone() ? 'bearer' : 'cookie');
  return mode === 'bearer' ? createBearerTokenStore() : createCookieTokenStore();
}
